import { Heading, Stack } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import fixture from '#modules/finder/features/posts/components/Filters/previews/fixture.json';
import { PostForm } from '#modules/finder/features/posts/components/PostForm';

/** Isolated form QA; catalog metadata intentionally includes obsolete minimums and maxima. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/post-form-options')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    previewLocale: search.previewLocale === 'en' ? ('en' as const) : ('ru' as const),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const { messages } =
      deps.previewLocale === 'en'
        ? await import('#features/i18n/locales/en/messages.po')
        : await import('#features/i18n/locales/ru/messages.po');
    return { ...deps, messages };
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: Preview,
});

function Preview() {
  const { previewLocale, messages } = Route.useLoaderData();
  const i18n = useMemo(
    () => setupI18n({ locale: previewLocale, messages: { [previewLocale]: messages } }),
    [previewLocale, messages]
  );
  return (
    <I18nProvider i18n={i18n}>
      <FormFixture key={previewLocale} locale={previewLocale} />
    </I18nProvider>
  );
}

const parameter = (
  key: string,
  title: string,
  labels: string[],
  selection: { multiple: boolean; maxSelect: number }
) => ({
  key,
  title,
  helperText: key === 'goals' ? title : undefined,
  ...selection,
  minSelect: 1,
  values: labels.map((name, index) => ({ id: `${key}-${index}`, name })),
});

function FormFixture({ locale }: { locale: 'ru' | 'en' }) {
  const data = fixture[locale];
  const [client] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: Infinity,
          queryFn: async () => {
            throw new Error('Isolated form QA');
          },
        },
      },
    });
    const defaultQueryOptions = client.defaultQueryOptions.bind(client);
    client.defaultQueryOptions = (options) => ({
      ...defaultQueryOptions(options),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      queryFn: async () => {
        throw new Error('Isolated form QA');
      },
    });
    client.setQueryData(['games'], [data.game]);
    client.setQueryData(
      ['platforms'],
      data.game.platformsIds.map((id, index) => ({
        id,
        name: ['PC', 'PLAYSTATION', 'XBOX'][index],
      }))
    );
    client.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: '12345678-1234-4234-8234-123456789abc',
      displayName: 'QA',
    });
    client.setQueryData(
      ['languages'],
      [
        { code: 'ru', name: 'Russian', nativeName: 'русский' },
        { code: 'en', name: 'English', nativeName: 'English' },
      ]
    );
    client.setQueryData(
      ['gameSettings', data.game.id],
      [
        {
          ...data.settings[0],
          helperText: data.settings[0].title,
          minSelect: 1,
          maxSelect: 1,
          multiple: false,
        },
        parameter(
          'goals',
          locale === 'ru' ? 'Цели' : 'Goals',
          locale === 'ru'
            ? [
                '🥇 Соревновательная игра',
                '🍕 Просто развлечься',
                '🎓 Улучшить навыки',
                '✌🏻 Найти друзей',
              ]
            : ['🥇 Competitive', '🍕 Casual fun', '🎓 Skill improvement', '✌🏻 Friendship'],
          { multiple: true, maxSelect: 4 }
        ),
        parameter(
          'role',
          locale === 'ru' ? 'Роль' : 'Role',
          locale === 'ru'
            ? ['💥 Штурмовик', '🛡️ Поддержка', '🎯 Снайпер', '👁️ Разведчик']
            : ['💥 Assault', '🛡️ Support', '🎯 Sniper', '👁️ Recon'],
          { multiple: false, maxSelect: 1 }
        ),
        parameter(
          'other',
          locale === 'ru' ? 'Что-нибудь ещё?' : 'Something else?',
          locale === 'ru'
            ? ['🔀 Кроссплей', '🎮 Геймпад', '🖲️ Мышь']
            : ['🔀 Crossplay', '🎮 Gamepad', '🖲️ Mouse'],
          { multiple: true, maxSelect: 10 }
        ),
        parameter(
          'mode',
          locale === 'ru' ? 'Режим игры' : 'Game mode',
          ['💣 Rush', '🔺 Escalation', '🏴 Domination', '🗺️ Conquest', '🎯 Team deathmatch'],
          { multiple: true, maxSelect: 4 }
        ),
        parameter(
          'limit',
          locale === 'ru' ? 'Не больше двух целей' : 'At most two goals',
          ['A', 'B', 'C', 'D'],
          { multiple: true, maxSelect: 2 }
        ),
      ]
    );
    return client;
  });
  return (
    <QueryClientProvider client={client}>
      <Stack p={{ base: 4, md: 8 }}>
        <PostForm
          isEditing
          isSubmitDisabled
          initialValues={{
            gameId: data.game.id,
            platform: data.game.platformsIds[1],
            mic: 'off',
            languages: ['ru', 'en'],
            text: '',
            gameSettings: {
              role: 'role-2',
              goals: ['goals-1', 'goals-2'],
              other: ['other-1'],
              mode: ['mode-0', 'mode-1', 'mode-2', 'mode-3'],
            },
          }}
          header={
            <Heading textStyle="heading">
              {locale === 'ru' ? 'Редактирование заявки' : 'Edit post'}
            </Heading>
          }
          onSubmit={async () => null}
        />
      </Stack>
    </QueryClientProvider>
  );
}
